import { TooltipProps } from "recharts";
import { Box } from "~/components/Box";
import { Flex } from "~/components/Flex";
import { Text } from "~/components/Text";

/**
 * Shared recharts tooltip for the dashboard-bi-v2 charts.
 *
 * Pass as `<Tooltip content={<ChartTooltip valueFormatter={...} />} />`.
 * Recharts injects `active` / `payload` / `label` at render time, so those
 * props are optional here. `valueFormatter` lets each chart format its values
 * (currency / percent / count / days).
 */
export interface ChartTooltipProps extends TooltipProps<number, string> {
  valueFormatter?: (value: number) => string;
}

export const ChartTooltip = ({
  active,
  payload,
  label,
  valueFormatter = (value) => String(value),
}: ChartTooltipProps) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <Box
      css={{
        backgroundColor: "$neutrals-white",
        border: "1px solid $neutral-200",
        borderRadius: "$md",
        boxShadow: "$sm",
        px: "$4",
        py: "$3",
        minWidth: "160px",
      }}
    >
      {label != null && label !== "" && (
        <Box css={{ mb: "$2" }}>
          <Text size="3" fw="700" css={{ color: "$neutrals-black" }}>
            {label}
          </Text>
        </Box>
      )}
      <Flex direction="column" gap="2">
        {payload.map((entry, index) => (
          <Flex
            key={`${entry.dataKey ?? entry.name ?? index}`}
            align="center"
            justify="between"
            gap="4"
          >
            <Flex align="center" gap="2">
              <Box
                css={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "$pill",
                  backgroundColor: entry.color ?? "$neutrals-base",
                  flexShrink: 0,
                }}
              />
              <Text size="2" css={{ color: "$neutrals-dark" }}>
                {entry.name}
              </Text>
            </Flex>
            <Text size="2" fw="600" css={{ color: "$neutrals-black" }}>
              {valueFormatter(Number(entry.value ?? 0))}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};

ChartTooltip.displayName = "ChartTooltip";
